/*
  <ts-cart-recs>: "Curated for you" row inside the cart drawer.
  - Order: pieces the shopper viewed this session first (most recent first), then Shopify's
    related picks to fill the row. Viewed pieces come from the same sessionStorage list the
    product page nudge writes ('tsb_viewed'), so there is no extra tracking.
  - Nothing is fetched on page load. Everything loads once, the first time the drawer opens.
  - Uses Shopify's built-in endpoints (/products/{handle}.js and /recommendations/products.json),
    no app.
  - "+" adds one item with /cart/add.js, then re-renders the drawer with the theme's own
    cart-drawer.renderContents(), the same path the product page Add to Bag uses.
*/
(function () {
  if (customElements.get('ts-cart-recs')) return;

  var VIEWED_KEY = 'tsb_viewed';
  var list = null;
  var pending = null;

  function esc(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function imgUrl(src, width) {
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function viewedHandles() {
    try {
      var stored = JSON.parse(sessionStorage.getItem(VIEWED_KEY) || '[]');
      if (!Array.isArray(stored)) return [];
      return stored
        .map(function (item) {
          return item && item.h;
        })
        .filter(Boolean)
        .slice(0, 4);
    } catch (e) {
      return [];
    }
  }

  function getJson(url) {
    return fetch(url)
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  class TsCartRecs extends HTMLElement {
    connectedCallback() {
      this.drawer = this.closest('cart-drawer');
      if (!this.drawer) return;

      this.onClick = this.onClick || this.handleClick.bind(this);
      this.addEventListener('click', this.onClick);

      if (list) {
        this.render();
        return;
      }
      if (this.drawer.classList.contains('active')) {
        this.load();
        return;
      }

      var self = this;
      this.observer = new MutationObserver(function () {
        if (self.drawer.classList.contains('active')) {
          self.observer.disconnect();
          self.load();
        }
      });
      this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.onClick) this.removeEventListener('click', this.onClick);
    }

    load() {
      var self = this;
      if (!pending) {
        var base = this.dataset.productBase || '/products/';
        var viewed = viewedHandles().map(function (handle) {
          return getJson(base + encodeURIComponent(handle) + '.js');
        });
        var related = getJson(
          this.dataset.url + '?product_id=' + encodeURIComponent(this.dataset.seed) + '&limit=10&intent=related'
        );

        pending = Promise.all([Promise.all(viewed), related]).then(function (results) {
          var merged = [];
          var seen = {};
          var add = function (p) {
            if (!p || seen[p.id]) return;
            seen[p.id] = true;
            merged.push(p);
          };
          results[0].forEach(add);
          ((results[1] && results[1].products) || []).forEach(add);
          list = merged;
        });
      }
      pending.then(function () {
        if (self.isConnected) self.render();
      });
    }

    render() {
      var grid = this.querySelector('.ts-recs__grid');
      if (!grid) return;

      var inCart = (this.dataset.cartIds || '').split(',');
      var max = Number(this.dataset.maxPrice) || Infinity;
      var items = (list || [])
        .filter(function (p) {
          return p.available && p.featured_image && p.price <= max && inCart.indexOf(String(p.id)) === -1;
        })
        .slice(0, 4);

      if (items.length < 2) {
        this.hidden = true;
        return;
      }

      var currency = this.dataset.currency || 'USD';
      var locale = document.documentElement.lang || 'en-US';

      grid.innerHTML = items
        .map(function (p) {
          var price = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: p.price % 100 === 0 ? 0 : 2,
            maximumFractionDigits: 2
          }).format(p.price / 100);
          var single = p.variants && p.variants.length === 1 && p.variants[0].available;
          return (
            '<li class="ts-recs__item">' +
            '<div class="ts-recs__media">' +
            '<a href="' + esc(p.url) + '" aria-label="' + esc(p.title) + '">' +
            '<img src="' + esc(imgUrl(p.featured_image, 240)) + '" alt="" width="120" height="120" loading="lazy" decoding="async">' +
            '</a>' +
            (single
              ? '<button type="button" class="ts-recs__add" data-variant="' + p.variants[0].id + '" aria-label="Add ' + esc(p.title) + ' to cart">+</button>'
              : '') +
            '</div>' +
            '<span class="ts-recs__price">' + esc(price) + '</span>' +
            '</li>'
          );
        })
        .join('');
      grid.removeAttribute('aria-busy');
    }

    handleClick(event) {
      var button = event.target.closest('.ts-recs__add');
      if (!button || button.disabled) return;

      var self = this;
      button.disabled = true;

      fetch(this.dataset.addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: Number(button.dataset.variant),
          quantity: 1,
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname
        })
      })
        .then(function (r) {
          return r.json();
        })
        .then(function (state) {
          if (state.status || !state.sections) throw state;
          self.drawer.renderContents(state);
        })
        .catch(function () {
          var price = button.closest('li').querySelector('.ts-recs__price');
          if (price) price.textContent = 'Unavailable';
          button.remove();
        });
    }
  }

  customElements.define('ts-cart-recs', TsCartRecs);
})();